import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

export type MovementKind = 'new' | 'up' | 'down' | 'same' | 'lost';

/**
 * Which way a ranking moved: ↑3 (better), ↓2 (worse), New, Lost. For search
 * positions a smaller number is better, so pass `from`/`to` as positions and
 * the direction is worked out here.
 */
export function Movement({
  kind,
  from,
  to,
  className,
}: {
  kind?: MovementKind;
  /** Previous position. */
  from?: number | null;
  /** Current position. */
  to?: number | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const steps = from != null && to != null ? from - to : null;
  const k: MovementKind = kind ?? (steps == null ? 'same' : steps > 0 ? 'up' : steps < 0 ? 'down' : 'same');

  if (k === 'new') {
    return <span className={cn('inline-flex items-center rounded-full border px-1.5 text-[11px] font-medium leading-5 text-success', className)}>{t('ANALYTICS_KIT.NEW')}</span>;
  }
  if (k === 'lost') {
    return <span className={cn('inline-flex items-center rounded-full border px-1.5 text-[11px] font-medium leading-5 text-destructive', className)}>{t('ANALYTICS_KIT.LOST')}</span>;
  }
  if (k === 'same' || !steps) {
    return (
      <span className={cn('inline-flex items-center text-xs text-muted-foreground', className)} aria-label={t('ANALYTICS_KIT.NO_CHANGE')}>
        <Minus className="size-3" />
      </span>
    );
  }
  const up = k === 'up';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium tabular-nums', up ? 'text-success' : 'text-destructive', className)}>
      {up ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
      {Math.abs(steps)}
    </span>
  );
}

/**
 * A 0–100 level as a short bar with its word — keyword difficulty, share of
 * searches, how complete a setup is. `invert` when high is bad.
 */
export function MeterBar({
  value,
  label,
  invert,
  className,
  width = 'w-16',
  high = 70,
  low = 40,
}: {
  value: number | null | undefined;
  label?: ReactNode;
  invert?: boolean;
  className?: string;
  width?: string;
  /** Where the colour turns: at or above `high`, below `low` — so a meter can match the words beside it. */
  high?: number;
  low?: number;
}) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  const v = Math.max(0, Math.min(100, value));
  const bad = invert ? v >= high : v < low;
  const good = invert ? v < low : v >= high;
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span className={cn('relative h-1.5 overflow-hidden rounded-full bg-muted', width)} aria-hidden>
        <span className={cn('absolute inset-y-0 start-0 rounded-full', bad ? 'bg-destructive' : good ? 'bg-success' : 'bg-warning')} style={{ width: `${Math.max(4, v)}%` }} />
      </span>
      <span className="text-xs tabular-nums text-muted-foreground">{label ?? Math.round(v)}</span>
    </span>
  );
}
